import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const nullableNumber = z.preprocess((val: unknown) => {
  if (val === "" || val === null || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}, z.number().nullable().optional());

const nullableInteger = z.preprocess((val: unknown) => {
  if (val === "" || val === null || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}, z.number().int().nullable().optional());

const nullableDate = z
  .preprocess((val) => parseToDateValue(val as string), dateSchema)
  .optional();

const nullableText = z.preprocess((val: unknown) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "string") {
    const trimmed = val.trim();
    return trimmed === "" ? null : trimmed;
  }
  return String(val);
}, z.string().nullable().optional());

export const strategyVersionFormSchema = z.object({
  accrued_interests: nullableNumber,
  accrued_interests_local_ccy: nullableNumber,
  balance_sheet: nullableNumber,
  balance_sheet_local_ccy: nullableNumber,
  book_cost: nullableNumber,
  book_cost_local_ccy: nullableNumber,
  cap: nullableNumber,
  code: nullableText,
  created_at: nullableText,
  date_next_coupon: nullableDate,
  days_next_coupon: nullableNumber,
  delta: nullableNumber,
  derivative_to_id: nullableInteger,
  dividends: nullableNumber,
  exposition: nullableNumber,
  exposition_local_ccy: nullableNumber,
  exposition_with_offset_local_ccy: nullableNumber,
  exposition_with_offset_ptf_ccy: nullableNumber,
  exposition_with_offset_without_accrued_local_ccy: nullableNumber,
  exposition_with_offset_without_accrued_ptf_ccy: nullableNumber,
  floor: nullableNumber,
  fx_rate: nullableNumber,
  instrument_pricing_date: nullableDate,
  market_value: nullableNumber,
  market_value_local_ccy: nullableNumber,
  market_value_local_ccy_without_accrued: nullableNumber,
  market_value_without_accrued: nullableNumber,
  price: nullableNumber,
  price_local_ccy: nullableNumber,
  quantity: nullableNumber,
  share_class: nullableText,
  strategy_id: nullableInteger,
  unit_book_cost: nullableNumber,
  unit_book_cost_local_ccy: nullableNumber,
  updated_at: nullableText,
  version_id: nullableInteger,
  weight: nullableNumber,
  weight_exposition: nullableNumber,
  weight_exposition_with_offset: nullableNumber,
  weight_exposition_without_offset: nullableNumber,
});

export type StrategyVersionFormValues = z.infer<
  typeof strategyVersionFormSchema
>;

type FieldKind = "number" | "text" | "date";

type StrategyVersionField = {
  name: keyof StrategyVersionFormValues;
  kind: FieldKind;
  disabled?: boolean;
};

const rows: StrategyVersionField[][] = [
  [
    { name: "code", kind: "text" },
    { name: "share_class", kind: "text" },
  ],
  [
    { name: "strategy_id", kind: "number" },
    { name: "version_id", kind: "number" },
  ],
  [
    { name: "weight", kind: "number" },
    { name: "quantity", kind: "number" },
  ],
  [
    { name: "cap", kind: "number" },
    { name: "floor", kind: "number" },
  ],
  [
    { name: "market_value", kind: "number" },
    { name: "market_value_without_accrued", kind: "number" },
  ],
  [
    { name: "balance_sheet", kind: "number" },
    { name: "exposition", kind: "number" },
  ],
  [
    { name: "price", kind: "number" },
    { name: "accrued_interests", kind: "number" },
  ],
  [
    { name: "book_cost", kind: "number" },
    { name: "unit_book_cost", kind: "number" },
  ],
  [
    { name: "dividends", kind: "number" },
    { name: "delta", kind: "number" },
  ],
  [
    { name: "market_value_local_ccy", kind: "number" },
    { name: "market_value_local_ccy_without_accrued", kind: "number" },
  ],
  [
    { name: "balance_sheet_local_ccy", kind: "number" },
    { name: "exposition_local_ccy", kind: "number" },
  ],
  [
    { name: "price_local_ccy", kind: "number" },
    { name: "accrued_interests_local_ccy", kind: "number" },
  ],
  [
    { name: "book_cost_local_ccy", kind: "number" },
    { name: "unit_book_cost_local_ccy", kind: "number" },
  ],
  [
    { name: "weight_exposition", kind: "number" },
    { name: "weight_exposition_with_offset", kind: "number" },
  ],
  [
    { name: "weight_exposition_without_offset", kind: "number" },
    { name: "fx_rate", kind: "number" },
  ],
  [
    { name: "exposition_with_offset_local_ccy", kind: "number" },
    { name: "exposition_with_offset_ptf_ccy", kind: "number" },
  ],
  [
    {
      name: "exposition_with_offset_without_accrued_local_ccy",
      kind: "number",
    },
    { name: "exposition_with_offset_without_accrued_ptf_ccy", kind: "number" },
  ],
  [
    { name: "days_next_coupon", kind: "number" },
    { name: "derivative_to_id", kind: "number" },
  ],
  [
    { name: "date_next_coupon", kind: "date" },
    { name: "instrument_pricing_date", kind: "date" },
  ],
  [
    { name: "created_at", kind: "text", disabled: true },
    { name: "updated_at", kind: "text", disabled: true },
  ],
];

const toLabel = (raw: string) =>
  raw
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const tr = (
  t: (key: string, opts?: any) => string,
  tableName: string,
  key: string,
) =>
  t(`${tableName}.columns.${key}`, {
    ns: "tables",
    defaultValue: toLabel(key),
  });

function renderField(
  field: StrategyVersionField,
  control: Control<StrategyVersionFormValues>,
  tableName: string,
  t: (key: string, opts?: any) => string,
) {
  const label = tr(t, tableName, field.name);
  if (field.kind === "date") {
    return (
      <DatePickerField
        key={field.name}
        name={field.name}
        label={label}
        control={control}
        fieldOptions={field.disabled ? { isDisabled: true } : undefined}
      />
    );
  }

  return (
    <InputField
      key={field.name}
      name={field.name}
      label={label}
      type={field.kind === "number" ? "number" : "text"}
      control={control}
      fieldOptions={field.disabled ? { isDisabled: true } : undefined}
    />
  );
}

export function StrategyVersionFormFields({
  control,
  table_name,
}: {
  control: Control<StrategyVersionFormValues>;
  table_name: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {row.map((field) => renderField(field, control, table_name, t))}
        </div>
      ))}
    </div>
  );
}
